import React, { useRef } from 'react';
import './DocumentEditor.scss';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { Document } from 'lib/types';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import HtmlLoaderPlugin from './HtmlLoader';
import { LexicalEditor } from 'lexical';
import { EditorRefPlugin } from '@lexical/react/LexicalEditorRefPlugin';
import { $generateHtmlFromNodes } from '@lexical/html';
import { ListItemNode, ListNode } from '@lexical/list';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { LinkNode } from '@lexical/link';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import ImageNode from './ImageNode';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import InsertImagePlugin from './InsertImagePlugin';
import ButtonComponent from 'components/ButtonComponent';
import { removeLinks } from 'lib/communication/news';
import Toolbar from './Toolbar/Toolbar';
import { htmlImportOverrides } from './htmlImportOverrides';
import { clearTrackedLinks, getTrackedLinks } from './linkRegistry';

interface DocumentEditorProps {
  document?: Document;
  onSubmit: (content: string, links: string[]) => void;
  onCancel: () => void;
}

const initialConfig = {
  namespace: 'DocumentEditor',
  onError: (error: Error) => {
    throw error;
  },
  nodes: [
    ListNode, ListItemNode,
    HeadingNode, QuoteNode,
    LinkNode,
    ImageNode
  ],
  html: { import: htmlImportOverrides },
};

export default function DocumentEditor({ document, onSubmit, onCancel }: DocumentEditorProps) {

  const editor = useRef<LexicalEditor | null>(null);
  const contentEditable = <ContentEditable className='ContentEditable' />;

  const handleSave = () => {
    if (!editor.current) return;
    editor.current.getEditorState().read(() => {
      const html = $generateHtmlFromNodes(editor.current!);
      const links = Array.from(new Set([
        ...(document?.links ?? []),
        ...getTrackedLinks(editor.current!),
      ]))
      const newLinks = links.filter((link) => {
        if (html.includes(link)) return true;
        removeLinks(document?.id ?? -1, link);
        return false;
      });
      onSubmit(html, newLinks);
    });
  }

  const handleCancel = () => {
    if (editor.current){
      getTrackedLinks(editor.current)
        .forEach(link => removeLinks(document?.id ?? -1 , link));
      clearTrackedLinks(editor.current);
    }
    onCancel();
  }

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <div style={{ marginBottom: '0.5rem' }}>
        <span>Treść</span>
      </div>
      <Toolbar />
      <RichTextPlugin
        contentEditable={contentEditable}
        ErrorBoundary={LexicalErrorBoundary}
      />
      <HtmlLoaderPlugin content={document?.content || ''} />
      <EditorRefPlugin editorRef={editor} />
      <InsertImagePlugin />
      <ListPlugin />
      <LinkPlugin />
      <HistoryPlugin />
      <ButtonComponent
        marginLeft='0'
        width='8em'
        marginRight='14px'
        text='Anuluj'
        onClick={handleCancel}
        onHoverAnimation={3}
      />
      <ButtonComponent
        marginLeft='0'
        marginRight='0'
        width='8em'
        text='Zapisz'
        onHoverAnimation={3}
        onClick={handleSave}
      />
    </LexicalComposer>
  );
}

